import styled from 'styled-components';

import CoreTooltip from 'shared/CoreTooltip';

// 바 차트 데이터의 타입 정의
/**
 * SymmetricVerticalBarChartDataItem 인터페이스는 대칭형 세로 막대 차트의 데이터 항목을 정의합니다.
 *
 * @property {number} [value] - 막대의 값입니다. 선택적 속성입니다.
 * @property {string} [valueColor] - 값 텍스트의 색상입니다. 선택적 속성입니다.
 * @property {string} [barColor] - 막대의 색상입니다. 선택적 속성입니다.
 * @property {string} [tooltip] - 막대에 마우스를 올렸을 때 표시될 툴팁 텍스트입니다. 선택적 속성입니다.
 * @property {number} [valuePercentage] - 값의 백분율입니다. 선택적 속성입니다.
 */
export interface SymmetricVerticalBarChartDataItem {
	value?: number | string;
	valueColor?: string;
	barColor?: string;
	tooltip?: string;
	valuePercentage?: number;
}

// 바 차트 컴포넌트의 props 타입 정의
export interface SymmetricVerticalBarChartProps {
	categories?: string[];
	categoriesColor?: string;
	topDataLabel?: string;
	bottomDataLabel?: string;
	topData?: SymmetricVerticalBarChartDataItem[];
	bottomData?: SymmetricVerticalBarChartDataItem[];
	barWidth?: number;
	barColor?: string;
	isLoading?: boolean;
	isError?: boolean;
	lackOfPermission?: boolean;
}

const SymmetricVerticalBarChart = ({
	categories,
	categoriesColor,
	topDataLabel,
	bottomDataLabel,
	barWidth = 58,
	isLoading,
	isError,
	lackOfPermission,
	...props
}: SymmetricVerticalBarChartProps) => {
	const topData = props?.topData
		? isLoading || isError
			? props.topData.map((data) => ({ ...data, valuePercentage: 0 }))
			: lackOfPermission
			? props.topData.map((data) => ({ ...data, valuePercentage: 0.5 }))
			: props.topData
		: [];

	const bottomData = props?.bottomData
		? isLoading || isError
			? props.bottomData.map((data) => ({ ...data, valuePercentage: 0 }))
			: lackOfPermission
			? props.bottomData.map((data) => ({ ...data, valuePercentage: 0.5 }))
			: props.bottomData
		: [];

	return (
		<Container>
			<LabelContainer>
				{topDataLabel && <Label>{topDataLabel}</Label>}
				{bottomDataLabel && <Label>{bottomDataLabel}</Label>}
			</LabelContainer>

			<ChartContainer>
				{categories?.map((category, idx) => (
					<ChartBarContainer key={idx}>
						{topData?.[idx]?.valuePercentage !== undefined && (
							<CoreTooltip
								_disabled={!topData?.[idx]?.tooltip}
								text={topData?.[idx]?.tooltip}
							>
								<ChartBarWrapper placement="top" barWidth={barWidth}>
									<ChartBar
										color={topData?.[idx]?.barColor}
										height={`${(topData?.[idx]?.valuePercentage ?? 0) * 65}px`}
										barWidth={barWidth}
									>
										{topData?.[idx]?.value && (
											<Value color={topData?.[idx]?.valueColor}>
												{topData?.[idx]?.value}
											</Value>
										)}
									</ChartBar>
								</ChartBarWrapper>
							</CoreTooltip>
						)}

						{category && <Category color={categoriesColor}>{category}</Category>}

						{bottomData?.[idx]?.valuePercentage !== undefined && (
							<CoreTooltip
								_disabled={!bottomData?.[idx]?.tooltip}
								text={bottomData?.[idx]?.tooltip}
							>
								<ChartBarWrapper placement="bottom" barWidth={barWidth}>
									<ChartBar
										color={bottomData?.[idx]?.barColor}
										height={`${(bottomData?.[idx]?.valuePercentage ?? 0) * 65}px`}
										barWidth={barWidth}
									>
										{bottomData?.[idx]?.value && (
											<Value color={bottomData?.[idx]?.valueColor}>
												{bottomData?.[idx]?.value}
											</Value>
										)}
									</ChartBar>
								</ChartBarWrapper>
							</CoreTooltip>
						)}
					</ChartBarContainer>
				))}
			</ChartContainer>
		</Container>
	);
};

export default SymmetricVerticalBarChart;

export const Container = styled.div`
	display: flex;
	gap: 16px;
`;

export const LabelContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	gap: 30px;
`;

export const Label = styled.div`
	height: 56px;
	display: flex;
	justify-content: center;
	align-items: center;
	color: #333;
	font-size: 14px;
`;

export const ChartContainer = styled.div`
	display: flex;
	gap: 8px;
`;

export const ChartBarContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 12px;
`;

export const Value = styled.div<{ color?: string }>`
	width: 100%;
	text-align: center;
	color: ${(props) => props.color || '#999'};
	font-size: 14px;
`;

export const Category = styled.div<{ color?: string }>`
	width: 100%;
	text-align: center;
	color: ${(props) => props.color || '#666'};
	font-size: 12px;
`;

export const ChartBarWrapper = styled.div<{
	placement: 'top' | 'bottom';
	barWidth: number;
}>`
	width: ${(props) => props.barWidth}px;
	height: 65px;
	background-color: #eee; /* Default bar background color */
	display: flex;
	justify-content: center;
	align-items: ${(props) => (props.placement === 'top' ? 'flex-end' : 'flex-start')};
`;

export const ChartBar = styled.div<{ color?: string; height: string; barWidth: number }>`
	width: ${(props) => props.barWidth}px;
	height: ${(props) => props.height};
	background-color: ${(props) => props.color || '#ccc'};
	transition: height 0.3s;
	display: flex;
	justify-content: center;
	align-items: center;
`;
